<template>
	<div class="resources-page">

		<section class="resources-hero">
			<div class="resources-content center-content">
				<h1>Resources</h1>
				<p>Helpful information for planning, funding, and managing underwater monitoring projects.</p>
			</div>
		</section>

		<section id="faq" class="resources-section faq-section">
			<div class="resources-content">
				<div class="pink-line"></div>
				<h2>FAQ</h2>

				<div class="faq-grid">
					<div class="resource-card" v-for="item in faqs" :key="item.q">
						<h3>{{ item.q }}</h3>
						<p>{{ item.a }}</p>
					</div>
				</div>
			</div>
		</section>

		<section class="resources-section funding-section">
			<div class="resources-content">
				<div class="pink-line"></div>
				<h2>Funding Your Underwater Monitoring Project</h2>

				<p class="large-copy">
					Many successful Fathom Ocean deployments are funded through grants,
					sponsorships, donations, educational initiatives, research budgets,
					and community partnerships.
				</p>

				<div class="resource-grid">
					<div class="resource-card">
						<h3>Research Grants</h3>
						<p>Marine science, ecosystem monitoring, habitat assessment, and species observation.</p>
					</div>

					<div class="resource-card">
						<h3>Educational Funding</h3>
						<p>K-12 programs, STEM initiatives, university outreach, and public education.</p>
					</div>

					<div class="resource-card">
						<h3>Conservation Funding</h3>
						<p>Habitat restoration, stewardship, public engagement, and ecosystem protection.</p>
					</div>

					<div class="resource-card">
						<h3>Community Sponsorships</h3>
						<p>Local businesses, tourism organizations, waterfront developments, and donors.</p>
					</div>

					<div class="resource-card">
						<h3>Infrastructure & Capital Projects</h3>
						<p>Utilities, ports, marinas, municipalities, and government agencies.</p>
					</div>
					<div class="resource-card">
						<h3>Revenue Generation & Sponsorship</h3>
						<p>
							Premium memberships, corporate sponsorships, advertising, and branded
							partnerships that generate ongoing support.
						</p>
</div>
				</div>
			</div>
		</section>

		<section class="resources-section project-support-section">
			<div class="resources-content">
				<div class="pink-line"></div>
				<h2>How Fathom Ocean Can Help</h2>

				<div class="resource-grid">
					<div class="resource-card">Budget estimates</div>
					<div class="resource-card">Technical specifications</div>
					<div class="resource-card">Product information</div>
					<div class="resource-card">Case studies</div>
					<div class="resource-card">Deployment examples</div>
					<div class="resource-card">Educational materials</div>
					<div class="resource-card">Fundraising support resources</div>
				</div>
			</div>
		</section>

		<section class="resources-section documents-section">
			<div class="resources-content">
				<div class="pink-line"></div>
				<h2>Technical Documents</h2>

				<p class="large-copy">
					Download spec sheets and product documents for Angler™, Octopus™, and other Fathom Ocean systems.
				</p>

				<div class="resource-grid">
					<div class="resource-card">
						<h3>Angler™ Spec Sheet</h3>
						<p>Coming soon.</p>
					</div>

					<div class="resource-card">
						<h3>Octopus™ Spec Sheet</h3>
						<p>Coming soon.</p>
					</div>

					<div class="resource-card">
						<h3>Fundraising Toolkit</h3>
						<p>Coming soon.</p>
					</div>
				</div>
			</div>
		</section>

		<section class="resources-section partnerships-section">
			<div class="resources-content">
				<div class="pink-line"></div>
				<h2>Case Studies</h2>

				<div class="partnership-grid">
					<div class="partnership-card">
						<img src="@/assets/images/cg_banner.png" alt="Coral Gardeners" loading="lazy" decoding="async">
						<h3>The Coral Gardeners, Mo'orea</h3>
						<p>
							Fathom is a proud partner of the Coral Gardeners, an international collective
							determined to save reefs through science, technology, and collaborative action.
						</p>
					</div>

					<div class="partnership-card">
						<img src="@/assets/images/hawaii_banner.png" alt="MEGA Lab Hawaii" loading="lazy" decoding="async">
						<h3>MEGA Lab Hawaii Reef Observatory</h3>
						<p>
							MEGA Lab is a global consortium of scientists, athletes, and artists working
							to create innovative solutions that protect our oceans.
						</p>
					</div>
				</div>
			</div>
		</section>

		<section class="resources-section field-tested-section">
			<div class="resources-content">
				<div class="pink-line"></div>
				<h2>Field-Tested for Conservation</h2>

				<div class="feature-grid">
					<div>
						<h3>AI-Powered Observation</h3>
						<p>On-board AI learns what to look for based on your criteria.</p>
					</div>
					<MotionMedia
						class="feature-media"
						:src="caymanVideo"
						:poster="caymanPoster"
					/>
				</div>

				<div class="feature-grid reverse">
					<img src="@/assets/images/octopus_wire_cleaning.png" alt="Self-cleaning camera" loading="lazy" decoding="async">
					<div>
						<h3>Self-Cleaning Technology</h3>
						<p>
							Our patented self-cleaning technology reduces maintenance and limits disruption
							to long-term monitoring and data collection.
						</p>
					</div>
				</div>

				<div class="feature-grid">
					<div>
						<h3>Sensor Integration</h3>
						<p>
							Pair systems with hydrophones, temperature sensors, and environmental monitoring tools.
						</p>
					</div>
					<img src="@/assets/images/humpback.png" alt="Humpback whale" loading="lazy" decoding="async">
				</div>
			</div>
		</section>

		<section class="resources-section consult-section center-content">
			<div class="resources-content">
				<h2>Need Help Building Your Project?</h2>
				<p class="large-copy">
					Our team is happy to discuss funding approaches, sponsorship opportunities,
					and project planning resources.
				</p>

				<b-button @click="$router.push('/contact')">
					Schedule a Consultation
				</b-button>
			</div>
		</section>

	</div>
</template>

<script>
import MotionMedia from '@/components/journey/MotionMedia.vue'
import caymanVideo from '@/assets/videos/cayman-machine-vision.mp4'
import caymanPoster from '@/assets/images/video-posters/cayman-machine-vision-poster.jpg'

export default {
	name: 'Resources',
	components: { MotionMedia },
	data() {
		return {
			caymanVideo,
			caymanPoster,
			faqs: [
				{ q: 'How do Fathom Ocean cameras stay clean underwater?', a: 'Our proprietary CleanSweep technology continuously removes biofouling and debris from the viewing surface.' },
				{ q: 'Can I access the camera remotely?', a: 'Yes. View your camera from anywhere with an internet connection.' },
				{ q: 'Can the cameras stream live to a website?', a: 'Yes. Public and private livestream options are available.' },
				{ q: 'Can the systems record video?', a: 'Yes. Recording and archival options are available.' },
				{ q: 'What power is required?', a: 'Systems can operate on standard AC power or low-voltage DC power.' },
				{ q: 'Can the system work with Starlink?', a: 'Yes.' },
				{ q: 'How deep can the cameras operate?', a: 'Standard systems are commonly deployed to approximately 50 meters, with custom configurations available.' },
				{ q: 'How often is maintenance required?', a: 'Maintenance intervals vary by environment and deployment goals.' },
				{ q: 'Do the systems work at night?', a: 'Yes. Optional underwater illumination is available.' },
				{ q: 'Can AI detect marine life automatically?', a: 'Yes. Detection and alerting capabilities can be customized for specific applications.' }
			]
		}
	}
}
</script>
